import { relations } from 'drizzle-orm';
import type { AnyPgColumn } from 'drizzle-orm/pg-core';
import {
  boolean,
  customType,
  date,
  doublePrecision,
  index,
  integer,
  jsonb,
  pgEnum,
  pgTable,
  primaryKey,
  text,
  timestamp,
  uniqueIndex,
  uuid,
  varchar,
  decimal,
  serial
} from 'drizzle-orm/pg-core';
import type { AdapterAccountType } from 'next-auth/adapters';
import { sql } from 'drizzle-orm';
// import { organizationTable } from "./organization"; // Adjust import if necessary - Assuming it's in the same file

// Export types for the accounting tables
export type AccountingEntry = typeof accountingEntryTable.$inferSelect
export type AccountingEntryLine = typeof accountingEntryLineTable.$inferSelect
export type Invoice = typeof invoiceTable.$inferSelect
export type BankTransaction = typeof bankTransactionTable.$inferSelect
export type UsageEvent = typeof usageEventsTable.$inferSelect
export type InvoiceUpload = typeof invoiceUploadTable.$inferSelect

export type AccountingEntryWithRelations = AccountingEntry & {
  lines: AccountingEntryLine[];
  invoice?: Invoice | null;
  // Populated manually by data loaders (drizzle relations cannot follow an
  // array column), sorted by bookingDate ascending. May contain fewer rows
  // than bankTransactionIds if an id dangles (deleted transaction).
  bankTransactions?: BankTransaction[];
}

// Invoicing (outgoing e-invoices)
export type Contact = typeof contactTable.$inferSelect
export type InvoiceNumberRange = typeof invoiceNumberRangeTable.$inferSelect
export type OutgoingInvoice = typeof outgoingInvoiceTable.$inferSelect
export type OutgoingInvoiceLine = typeof outgoingInvoiceLineTable.$inferSelect

export type OutgoingInvoiceWithRelations = OutgoingInvoice & {
  lines: OutgoingInvoiceLine[];
  contact: Contact;
}

// Custom Types
export const bytea = customType<{
  data: Buffer | null;
  notNull: false;
  default: false;
}>({
  dataType() {
    return 'bytea';
  },
  toDriver(val: Buffer | null) {
    return val;
  },
  fromDriver(value: unknown) {
    if (value === null) {
      return null;
    }

    if (value instanceof Buffer) {
      return value;
    }

    if (typeof value === 'string') {
      return Buffer.from(value, 'hex');
    }

    throw new Error(`Unexpected type received from driver: ${typeof value}`);
  }
});



// Enums
export enum ActionType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete'
}

export enum ActorType {
  SYSTEM = 'system',
  MEMBER = 'member',
  API = 'api'
}

// Contact-related enums removed during CRM cleanup

// DayOfWeek enum removed during business hours cleanup - not needed for accounting app
// export enum DayOfWeek {
//   SUNDAY = 'sunday',
//   MONDAY = 'monday',
//   TUESDAY = 'tuesday',
//   WEDNESDAY = 'wednesday',
//   THURSDAY = 'thursday',
//   FRIDAY = 'friday',
//   SATURDAY = 'saturday'
// }

export enum FeedbackCategory {
  SUGGESTION = 'suggestion',
  PROBLEM = 'problem',
  QUESTION = 'question'
}

export enum InvitationStatus {
  PENDING = 'pending',
  ACCEPTED = 'accepted',
  REVOKED = 'revoked'
}

export enum Role {
  MEMBER = 'member',
  ADMIN = 'admin'
}

export enum WebhookTrigger {
  // Contact triggers removed during CRM cleanup
  // Add accounting/invoice-related webhook triggers here when needed
  PLACEHOLDER = 'placeholder' // Temporary placeholder to prevent empty enum
}

export enum PostingStatus {
  DRAFT = 'DRAFT',
  INVOICE_PROCESSED = 'INVOICE_PROCESSED',
  PAYMENT_MATCHED = 'PAYMENT_MATCHED',
  LOCKED = 'LOCKED',
  CANCELLED = 'CANCELLED'
}

export enum InvoiceStatus {
  OPEN = 'OPEN',
  PAID = 'PAID',
  CANCELLED = 'CANCELLED',
  // New values must be appended: Postgres ALTER TYPE ADD VALUE appends,
  // and index-based access to enumValues depends on this order.
  REVIEW_NEEDED = 'REVIEW_NEEDED',
  PARTIALLY_PAID = 'PARTIALLY_PAID'
}

export enum InvoiceUploadStatus {
  QUEUED = 'QUEUED',
  PROCESSING = 'PROCESSING',
  DONE = 'DONE',
  FAILED = 'FAILED'
}

export enum ContactType {
  CUSTOMER = 'CUSTOMER',
  SUPPLIER = 'SUPPLIER',
  BOTH = 'BOTH'
}

export enum InvoiceComplianceStandard {
  /** ZUGFeRD / Factur-X EN 16931 — the B2B baseline */
  EN16931 = 'EN16931',
  /** Additionally enforces the XRechnung/Peppol-layer fields (B2G-ready) */
  XRECHNUNG = 'XRECHNUNG'
}

export enum OutgoingInvoiceStatus {
  /** Editable working copy — has no invoice number yet */
  DRAFT = 'DRAFT',
  /** Number assigned, totals frozen, booked — immutable (GoBD) */
  ISSUED = 'ISSUED',
  /** Cancelled via correction document (correctsInvoiceId on the Storno) */
  CANCELLED = 'CANCELLED'
}

function enumToPgEnum<T extends Record<string, string>>(myEnum: T) {
  return Object.values(myEnum).map((value) => `${value}`) as [
    T[keyof T],
    ...T[keyof T][]
  ];
}

// use lowercase: https://github.com/drizzle-team/drizzle-orm/issues/1564#issuecomment-2320605690

export const actionTypeEnum = pgEnum('actiontype', enumToPgEnum(ActionType));
export const actorTypeEnum = pgEnum('actortype', enumToPgEnum(ActorType));
// Contact-related pgEnums removed during CRM cleanup
// export const dayOfWeekEnum = pgEnum('dayofweek', enumToPgEnum(DayOfWeek)); // Removed during business hours cleanup
export const feedbackCategoryEnum = pgEnum(
  'feedbackcategory',
  enumToPgEnum(FeedbackCategory)
);
export const invitationStatusEnum = pgEnum(
  'invitationstatus',
  enumToPgEnum(InvitationStatus)
);
export const roleEnum = pgEnum('Role', enumToPgEnum(Role));
export const webhookTriggerEnum = pgEnum(
  'webhooktrigger',
  enumToPgEnum(WebhookTrigger)
);
export const postingStatusEnum = pgEnum('postingstatus', enumToPgEnum(PostingStatus));
export const invoiceStatusEnum = pgEnum('invoicestatus', enumToPgEnum(InvoiceStatus));
export const invoiceUploadStatusEnum = pgEnum(
  'invoiceuploadstatus',
  enumToPgEnum(InvoiceUploadStatus)
);
export const contactTypeEnum = pgEnum('contacttype', enumToPgEnum(ContactType));
export const outgoingInvoiceStatusEnum = pgEnum(
  'outgoinginvoicestatus',
  enumToPgEnum(OutgoingInvoiceStatus)
);
export const invoiceComplianceStandardEnum = pgEnum(
  'invoicecompliancestandard',
  enumToPgEnum(InvoiceComplianceStandard)
);

// Add new Enums
export const bankProviderEnum = pgEnum('bank_provider', ['gocardless', 'csv_import']);
export const bankConnectionStatusEnum = pgEnum('bank_connection_status', ['linking', 'active', 'expired', 'error', 'reconnecting']);

// Tables

export const apiKeyTable = pgTable(
  'apiKey',
  {
    id: uuid('id').primaryKey().notNull().defaultRandom(),
    organizationId: uuid('organizationId')
      .notNull()
      .references(() => organizationTable.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade'
      }),
    description: varchar('description', { length: 70 }).notNull(),
    hashedKey: text('hashedKey').notNull(),
    expiresAt: timestamp('expiresAt', { precision: 3, mode: 'date' }),
    lastUsedAt: timestamp('lastUsedAt', { precision: 3, mode: 'date' }),
    createdAt: timestamp('createdAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull(),
    updatedAt: timestamp('updatedAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull()
      .$onUpdate(() => new Date())
  },
  (table) => [
    uniqueIndex('IX_apiKey_hashedKey_unique').using(
      'btree',
      table.hashedKey.asc().nullsLast().op('text_ops')
    ),
    index('IX_apiKey_organizationId').using(
      'btree',
      table.organizationId.asc().nullsLast().op('uuid_ops')
    )
  ]
);

export const authenticatorAppTable = pgTable(
  'authenticatorApp',
  {
    id: uuid('id').primaryKey().notNull().defaultRandom(),
    userId: uuid('userId')
      .notNull()
      .references(() => userTable.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade'
      }),
    accountName: varchar('accountName', { length: 255 }).notNull(),
    issuer: varchar('issuer', { length: 255 }).notNull(),
    secret: varchar('secret', { length: 255 }).notNull(),
    recoveryCodes: varchar('recoveryCodes', { length: 1024 }).notNull(),
    createdAt: timestamp('createdAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull(),
    updatedAt: timestamp('updatedAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull()
      .$onUpdate(() => new Date())
  },
  (table) => [
    uniqueIndex('IX_authenticatorApp_userId_unique').using(
      'btree',
      table.userId.asc().nullsLast().op('uuid_ops')
    )
  ]
);

export const changeEmailRequestTable = pgTable(
  'changeEmailRequest',
  {
    id: uuid('id').primaryKey().notNull().defaultRandom(),
    userId: uuid('userId')
      .notNull()
      .references(() => userTable.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade'
      }),
    email: text('email').notNull(),
    expires: timestamp('expires', { precision: 3, mode: 'date' }).notNull(),
    valid: boolean('valid').default(false).notNull(),
    createdAt: timestamp('createdAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull(),
    updatedAt: timestamp('updatedAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull()
      .$onUpdate(() => new Date())
  },
  (table) => [
    index('IX_changeEmailRequest_userId').using(
      'btree',
      table.userId.asc().nullsLast().op('uuid_ops')
    )
  ]
);

// Contact-related tables removed during CRM cleanup:
// - contactTable
// - contactCommentTable  
// - contactNoteTable
// - contactPageVisitTable
// - contactImageTable
// - contactTaskTable
// - favoriteTable

export const invitationTable = pgTable(
  'invitation',
  {
    id: uuid('id').primaryKey().notNull().defaultRandom(),
    organizationId: uuid('organizationId')
      .notNull()
      .references(() => organizationTable.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade'
      }),
    token: uuid('token').notNull().defaultRandom(),
    email: varchar('email', { length: 255 }).notNull(),
    role: roleEnum('role').default(Role.MEMBER).notNull(),
    status: invitationStatusEnum('status')
      .default(InvitationStatus.PENDING)
      .notNull(),
    lastSentAt: timestamp('lastSentAt', { precision: 3, mode: 'date' }),
    createdAt: timestamp('createdAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull(),
    updatedAt: timestamp('updatedAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull()
      .$onUpdate(() => new Date())
  },
  (table) => [
    index('IX_invitation_organizationId').using(
      'btree',
      table.organizationId.asc().nullsLast().op('uuid_ops')
    ),
    index('IX_invitation_token').using(
      'btree',
      table.token.asc().nullsLast().op('uuid_ops')
    )
  ]
);

// contactTagTable removed during CRM cleanup

export const accountTable = pgTable(
  'account',
  {
    id: uuid('id').primaryKey().notNull().defaultRandom(),
    userId: uuid('userId')
      .notNull()
      .references(() => userTable.id, { onDelete: 'cascade' }),
    type: text('type').$type<AdapterAccountType>().notNull(),
    provider: text('provider').notNull(),
    providerAccountId: text('providerAccountId').notNull(),
    refresh_token: text('refresh_token'),
    access_token: text('access_token'),
    expires_at: integer('expires_at'),
    token_type: text('token_type'),
    scope: text('scope'),
    id_token: text('id_token'),
    session_state: text('session_state'),
    createdAt: timestamp('createdAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull(),
    updatedAt: timestamp('updatedAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull()
      .$onUpdate(() => new Date())
  },
  (table) => [
    uniqueIndex('IX_account_provider_providerAccountId_unique').using(
      'btree',
      table.provider.asc().nullsLast().op('text_ops'),
      table.providerAccountId.asc().nullsLast().op('text_ops')
    ),
    index('IX_account_userId').using(
      'btree',
      table.userId.asc().nullsLast().op('uuid_ops')
    )
  ]
);

export const feedbackTable = pgTable(
  'feedback',
  {
    id: uuid('id').primaryKey().notNull().defaultRandom(),
    organizationId: uuid('organizationId')
      .notNull()
      .references(() => organizationTable.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade'
      }),
    userId: uuid('userId').references(() => userTable.id, {
      onDelete: 'set null',
      onUpdate: 'cascade'
    }),
    category: feedbackCategoryEnum('category')
      .default(FeedbackCategory.SUGGESTION)
      .notNull(),
    message: varchar('message', { length: 4000 }).notNull(),
    createdAt: timestamp('createdAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull(),
    updatedAt: timestamp('updatedAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull()
      .$onUpdate(() => new Date())
  },
  (table) => [
    index('IX_feedback_organizationId').using(
      'btree',
      table.organizationId.asc().nullsLast().op('uuid_ops')
    ),
    index('IX_feedback_userId').using(
      'btree',
      table.userId.asc().nullsLast().op('uuid_ops')
    )
  ]
);

export const notificationTable = pgTable(
  'notification',
  {
    id: uuid('id').primaryKey().notNull().defaultRandom(),
    userId: uuid('userId')
      .notNull()
      .references(() => userTable.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade'
      }),
    subject: varchar('subject', { length: 128 }),
    content: varchar('content', { length: 8000 }).notNull(),
    link: varchar('link', { length: 2000 }),
    seenAt: timestamp('seenAt', { precision: 3, mode: 'date' }),
    dismissed: boolean('dismissed').default(false).notNull(),
    createdAt: timestamp('createdAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull(),
    updatedAt: timestamp('updatedAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull()
      .$onUpdate(() => new Date())
  },
  (table) => [
    index('IX_notification_userId').using(
      'btree',
      table.userId.asc().nullsLast().op('uuid_ops')
    )
  ]
);

export const resetPasswordRequestTable = pgTable(
  'resetPasswordRequest',
  {
    id: uuid('id').primaryKey().notNull().defaultRandom(),
    email: text('email').notNull(),
    expires: timestamp('expires', { precision: 3, mode: 'date' }).notNull(),
    createdAt: timestamp('createdAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull(),
    updatedAt: timestamp('updatedAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull()
      .$onUpdate(() => new Date())
  },
  (table) => [
    index('IX_resetPasswordRequest_email').using(
      'btree',
      table.email.asc().nullsLast().op('text_ops')
    )
  ]
);

export const userImageTable = pgTable(
  'userImage',
  {
    id: uuid('id').primaryKey().notNull().defaultRandom(),
    userId: uuid('userId')
      .notNull()
      .references(() => userTable.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade'
      }),
    data: bytea('data'),
    contentType: varchar('contentType', { length: 255 }),
    hash: varchar('hash', { length: 64 })
  },
  (table) => [
    index('IX_userImage_userId').using(
      'btree',
      table.userId.asc().nullsLast().op('uuid_ops')
    )
  ]
);

export const userTable = pgTable(
  'user',
  {
    id: uuid('id')
      .primaryKey()
      .$defaultFn(() => crypto.randomUUID()),
    email: text('email').unique(),
    emailVerified: timestamp('emailVerified', { precision: 3, mode: 'date' }),
    password: varchar('password', { length: 60 }),
    lastLogin: timestamp('lastLogin', { precision: 3, mode: 'date' }),
    locale: varchar('locale', { length: 8 }).default('en-US').notNull(),
    completedOnboarding: boolean('completedOnboarding')
      .default(false)
      .notNull(),
    // TODO: Customize these notification types for your app
    // enabledContactsNotifications: boolean('enabledContactsNotifications')
    //   .default(false)
    //   .notNull(),
    // enabledInboxNotifications: boolean('enabledInboxNotifications')
    //   .default(false)
    //   .notNull(),
    enabledNewsletter: boolean('enabledNewsletter').default(false).notNull(),
    enabledProductUpdates: boolean('enabledProductUpdates')
      .default(false)
      .notNull(),
    // enabledWeeklySummary: boolean('enabledWeeklySummary')
    //   .default(false)
    //   .notNull(),
    image: varchar('image', { length: 2048 }),
    name: varchar('name', { length: 64 }).notNull(),
    phone: varchar('phone', { length: 32 }),
    createdAt: timestamp('createdAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull(),
    updatedAt: timestamp('updatedAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull()
      .$onUpdate(() => new Date())
  },
  (table) => [
    uniqueIndex('IX_user_email_unique').using(
      'btree',
      table.email.asc().nullsLast().op('text_ops')
    ),
    index('IX_user_name').using(
      'btree',
      table.name.asc().nullsLast().op('text_ops')
    )
  ]
);

export const sessionTable = pgTable(
  'session',
  {
    sessionToken: text('sessionToken').primaryKey(),
    userId: uuid('userId')
      .notNull()
      .references(() => userTable.id, { onDelete: 'cascade' }),
    expires: timestamp('expires', { mode: 'date' }).notNull(),
    createdAt: timestamp('createdAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull(),
    updatedAt: timestamp('updatedAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull()
      .$onUpdate(() => new Date())
  },
  (table) => [
    index('IX_session_userId').using(
      'btree',
      table.userId.asc().nullsLast().op('uuid_ops')
    )
  ]
);

export const verificationTokenTable = pgTable(
  'verificationToken',
  {
    identifier: text('identifier').notNull(),
    token: text('token').notNull(),
    expires: timestamp('expires', { precision: 3, mode: 'date' }).notNull()
  },
  (table) => [
    uniqueIndex('IX_verificationToken_identifier_unique').using(
      'btree',
      table.identifier.asc().nullsLast().op('text_ops'),
      table.token.asc().nullsLast().op('text_ops')
    )
  ]
);

// Business hours tables removed during cleanup - not needed for accounting app
// export const workHoursTable = pgTable(
//   'workHours',
//   {
//     id: uuid('id').primaryKey().notNull().defaultRandom(),
//     organizationId: uuid('organizationId')
//       .notNull()
//       .references(() => organizationTable.id, {
//         onDelete: 'cascade',
//         onUpdate: 'cascade'
//       }),
//     dayOfWeek: dayOfWeekEnum('dayOfWeek').notNull()
//   },
//   (table) => [
//     index('IX_workHours_organizationId').using(
//       'btree',
//       table.organizationId.asc().nullsLast().op('uuid_ops')
//     )
//   ]
// );

// export const workTimeSlotTable = pgTable(
//   'workTimeSlot',
//   {
//     id: uuid('id').primaryKey().notNull().defaultRandom(),
//     workHoursId: uuid('workHoursId')
//       .notNull()
//       .references(() => workHoursTable.id, {
//         onDelete: 'cascade',
//         onUpdate: 'cascade'
//       }),
//     start: timestamp('start', { precision: 3, mode: 'date' }).notNull(),
//     end: timestamp('end', { precision: 3, mode: 'date' }).notNull()
//   },
//   (table) => [
//     index('IX_workTimeSlot_workHoursId').using(
//       'btree',
//       table.workHoursId.asc().nullsLast().op('uuid_ops')
//     )
//   ]
// );

// contactActivityTable removed during CRM cleanup

export const webhookTable = pgTable(
  'webhook',
  {
    id: uuid('id').primaryKey().notNull().defaultRandom(),
    organizationId: uuid('organizationId')
      .notNull()
      .references(() => organizationTable.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade'
      }),
    secret: varchar('secret', { length: 1024 }),
    createdAt: timestamp('createdAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull(),
    updatedAt: timestamp('updatedAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull(),
    triggers: webhookTriggerEnum('triggers').array().notNull(),
    url: varchar('url', { length: 2000 }).notNull()
  },
  (table) => [
    {
      organizationIdIndex: index('IX_webhook_organizationId').on(
        table.organizationId
      )
    }
  ]
);

export const membershipTable = pgTable(
  'membership',
  {
    id: uuid('id').primaryKey().notNull().defaultRandom(),
    organizationId: uuid('organizationId')
      .notNull()
      .references(() => organizationTable.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade'
      }),
    userId: uuid('userId')
      .notNull()
      .references(() => userTable.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade'
      }),
    role: roleEnum('role').default(Role.MEMBER).notNull(),
    isOwner: boolean('isOwner').default(false).notNull(),
    createdAt: timestamp('createdAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull()
  },
  (table) => [
    uniqueIndex('IX_membership_organizationId_userId_unique').using(
      'btree',
      table.organizationId.asc().nullsLast().op('uuid_ops'),
      table.userId.asc().nullsLast().op('uuid_ops')
    ),
    index('IX_membership_userId').using(
      'btree',
      table.userId.asc().nullsLast().op('uuid_ops')
    ),
    index('IX_membership_organizationId').using(
      'btree',
      table.organizationId.asc().nullsLast().op('uuid_ops')
    )
  ]
);

export const organizationLogoTable = pgTable(
  'organizationLogo',
  {
    id: uuid('id').primaryKey().notNull().defaultRandom(),
    organizationId: uuid('organizationId')
      .notNull()
      .references(() => organizationTable.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade'
      }),
    // 'default' = square avatar logo; 'invoice' = wide letterhead logo
    kind: varchar('kind', { length: 16 }).default('default').notNull(),
    data: bytea('data'),
    contentType: varchar('contentType', { length: 255 }),
    hash: varchar('hash', { length: 64 })
  },
  (table) => [
    index('IX_organizationLogo_organizationId').using(
      'btree',
      table.organizationId.asc().nullsLast().op('uuid_ops')
    ),
    // One logo per kind and organization — readers assume a single row
    uniqueIndex('IX_organizationLogo_organizationId_kind_unique').using(
      'btree',
      table.organizationId.asc().nullsLast().op('uuid_ops'),
      table.kind.asc().nullsLast()
    )
  ]
);

/** Selectable PDF layout variants — all contain the same §14 UStG mandatory fields */
export type InvoiceTemplateLayout = 'classic' | 'modern' | 'minimal';

export type InvoiceLogoPosition = 'left' | 'center' | 'right';

/** Visual template for outgoing invoices — admins pick a layout and branding, content stays compliant */
export type InvoiceTemplate = {
  /** PDF layout variant; defaults to 'classic' */
  layout?: InvoiceTemplateLayout | null;
  /** Print the organization logo in the PDF header (requires PNG/JPEG logo) */
  showLogo: boolean;
  /** Horizontal logo placement in the header; defaults to 'right' */
  logoPosition?: InvoiceLogoPosition | null;
  /** Hex accent color (e.g. '#1d4ed8') for title/rules; null = neutral greyscale */
  accentColor: string | null;
  /** Prefilled into introText of new drafts */
  defaultIntroText?: string | null;
  /** Prefilled into outroText of new drafts */
  defaultOutroText?: string | null;
};

export const organizationTable = pgTable(
  'organization',
  {
    id: uuid('id').primaryKey().notNull().defaultRandom(),
    name: varchar('name', { length: 255 }).notNull(),
    email: varchar('email', { length: 255 }),
    website: varchar('website', { length: 2000 }),
    phone: varchar('phone', { length: 32 }),
    logo: varchar('logo', { length: 2048 }),
    slug: varchar('slug', { length: 255 }).notNull(),
    // Billing fields from upstream
    billingCustomerId: varchar('billingCustomerId', { length: 255 }),
    billingEmail: varchar('billingEmail', { length: 255 }),
    billingCompanyName: varchar('billingCompanyName', { length: 255 }),
    billingLine1: varchar('billingLine1', { length: 255 }),
    billingLine2: varchar('billingLine2', { length: 255 }),
    billingCountry: varchar('billingCountry', { length: 3 }),
    billingPostalCode: varchar('billingPostalCode', { length: 16 }),
    billingCity: varchar('billingCity', { length: 255 }),
    billingState: varchar('billingState', { length: 255 }),
    billingSpendingLimit: integer('billingSpendingLimit'), // Monthly spending limit in cents
    // Accounting fields from your branch
    accountingStandard: text('accountingStandard').default('SKR03').notNull(),
    taxId: varchar('taxId', { length: 255 }),
    vatId: varchar('vatId', { length: 255 }),
    isCashAccounting: boolean('isCashAccounting').default(true).notNull(),
    // DATEV settings
    datevConsultantNumber: varchar('datevConsultantNumber', { length: 12 }),
    datevClientNumber: varchar('datevClientNumber', { length: 12 }),
    datevFormatVersion: varchar('datevFormatVersion', { length: 8 }),
    // Invoicing (outgoing e-invoices). Seller address is deliberately separate
    // from the billing* fields — those hold the Stripe billing address.
    isSmallBusiness: boolean('isSmallBusiness').default(false).notNull(), // §19 UStG Kleinunternehmer
    // Payment details printed on outgoing invoices — entered directly
    // (bank connections often lack an IBAN, e.g. CSV imports)
    invoiceComplianceStandard: invoiceComplianceStandardEnum('invoiceComplianceStandard')
      .default(InvoiceComplianceStandard.EN16931)
      .notNull(),
    invoiceIban: varchar('invoiceIban', { length: 34 }),
    invoiceBic: varchar('invoiceBic', { length: 11 }),
    sellerLine1: varchar('sellerLine1', { length: 255 }),
    sellerLine2: varchar('sellerLine2', { length: 255 }),
    sellerPostalCode: varchar('sellerPostalCode', { length: 16 }),
    sellerCity: varchar('sellerCity', { length: 255 }),
    sellerCountry: varchar('sellerCountry', { length: 2 }),
    invoiceTemplate: jsonb('invoiceTemplate').$type<InvoiceTemplate>()
  },
  (table) => [
    index('IX_organization_billingCustomerId').using(
      'btree',
      table.billingCustomerId.asc().nullsLast().op('text_ops')
    ),
    uniqueIndex('IX_organization_slug_unique').using(
      'btree',
      table.slug.asc().nullsLast().op('text_ops')
    )
  ]
);

// Add new Bank Connection Table
export const bankConnectionTable = pgTable(
  'bankConnection',
  {
    id: uuid('id').primaryKey().notNull().defaultRandom(),
    organizationId: uuid('organizationId')
      .notNull()
      .references(() => organizationTable.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade'
      }),
    provider: bankProviderEnum('provider').notNull(),
    status: bankConnectionStatusEnum('status').notNull(),
    // Store provider-specific IDs like GoCardless requisitionId, Plaid itemId etc.
    providerMetadata: jsonb('providerMetadata'),
    // Store encrypted tokens securely
    encryptedAccessToken: text('encryptedAccessToken'),
    encryptedRefreshToken: text('encryptedRefreshToken'), // If applicable
    expiresAt: timestamp('expiresAt', { precision: 3, mode: 'date' }),
    lastAccessedAt: timestamp('lastAccessedAt', { precision: 3, mode: 'date' }),
    createdAt: timestamp('createdAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull(),
    updatedAt: timestamp('updatedAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull()
      .$onUpdate(() => new Date())
  }
);

// Modify Bank Account Data Table
export const bankAccountDataTable = pgTable(
  'bankAccountData',
  {
    id: uuid('id').primaryKey().notNull().defaultRandom(),
    organizationId: uuid('organizationId')
      .notNull()
      .references(() => organizationTable.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade'
      }),
    // Link to the connection table (nullable for manual/CSV accounts)
    bankConnectionId: uuid('bankConnectionId')
      .references(() => bankConnectionTable.id, {
        onDelete: 'set null',
        onUpdate: 'cascade'
      }),
    // Store the account ID from the provider (e.g., GoCardless)
    providerAccountId: text('providerAccountId'),
    name: varchar('name', { length: 255 }).notNull(),
    iban: varchar('iban', { length: 34 }),
    bic: varchar('bic', { length: 11 }),
    bankName: varchar('bankName', { length: 255 }),
    skrAccountNumber: varchar('skrAccountNumber', { length: 4 }).notNull(),
    currency: varchar('currency', { length: 3 }).default('EUR').notNull(),
    enabled: boolean('enabled').default(true).notNull(),
    lastSyncTimestamp: timestamp('lastSyncTimestamp', { precision: 3, mode: 'date' }),
    createdAt: timestamp('createdAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull(),
    updatedAt: timestamp('updatedAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull()
      .$onUpdate(() => new Date())
  },
  (table) => ({
    // Add unique constraint for organization + provider account combination
    orgProviderAccountIdx: uniqueIndex('org_provider_account_idx').on(
      table.organizationId,
      table.providerAccountId
    )
  })
);

// Modify Bank Transaction Table
export const bankTransactionTable = pgTable(
  'bankTransaction',
  {
    id: uuid('id').primaryKey().notNull().defaultRandom(),
    bankAccountDataId: uuid('bankAccountDataId')
      .notNull()
      .references(() => bankAccountDataTable.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade'
      }),
    // Store provider's unique ID for deduplication
    providerTransactionId: text('providerTransactionId'),
    bookingDate: timestamp('bookingDate', { precision: 3, mode: 'date' }).notNull(),
    counterparty: text('counterparty'),
    purpose: text('purpose'),
    amount: integer('amount').notNull(), // Consider using `numeric` or `decimal` for precision if needed
    processed: boolean('processed').default(false).notNull(),
    // Store raw provider data if needed for debugging or future use
    providerMetadata: jsonb('providerMetadata'),
    createdAt: timestamp('createdAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull(),
    updatedAt: timestamp('updatedAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull()
      .$onUpdate(() => new Date())
  },
  (table) => [
    index('idx_bank_transaction_account_date').on(
      table.bankAccountDataId,
      table.bookingDate
    ),
    index('idx_bank_transaction_processed').on(table.processed),
    // Ensure uniqueness based on the provider's transaction ID for a given account
    uniqueIndex('bankTransaction_account_provider_id_unique').on(
      table.bankAccountDataId,
      table.providerTransactionId
    ).where(sql`${table.providerTransactionId} IS NOT NULL`) // Only enforce if provider ID exists
  ]
);

// Permanent cache of historical FX rates: once published for a date they never
// change, so rows are written once and read forever (no invalidation).
export const exchangeRateTable = pgTable(
  'exchangeRate',
  {
    id: uuid('id').primaryKey().notNull().defaultRandom(),
    rateDate: date('rateDate', { mode: 'string' }).notNull(), // YYYY-MM-DD, as requested from the rate API
    baseCurrency: varchar('baseCurrency', { length: 3 }).notNull(),
    targetCurrency: varchar('targetCurrency', { length: 3 }).notNull(),
    rate: doublePrecision('rate').notNull(),
    createdAt: timestamp('createdAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull()
  },
  (table) => [
    uniqueIndex('exchangeRate_date_base_target_unique').on(
      table.rateDate,
      table.baseCurrency,
      table.targetCurrency
    )
  ]
);

export const invoiceTable = pgTable(
  'invoice',
  {
    id: uuid('id').primaryKey().notNull().defaultRandom(),
    organizationId: uuid('organizationId')
      .notNull()
      .references(() => organizationTable.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade'
      }),
    invoiceNumber: varchar('invoiceNumber', { length: 50 }).notNull(),
    bookingNumber: varchar('bookingNumber', { length: 50 }),
    invoiceDate: timestamp('invoiceDate', { precision: 3, mode: 'date' }).notNull(),
    serviceDate: timestamp('serviceDate', { precision: 3, mode: 'date' }),
    dueDate: timestamp('dueDate', { precision: 3, mode: 'date' }).notNull(),
    bookingDate: timestamp('bookingDate', { precision: 3, mode: 'date' }),
    vendorName: varchar('vendorName', { length: 255 }).notNull(),
    vendorVatId: varchar('vendorVatId', { length: 30 }),
    // ISO 3166-1 alpha-2, e.g. 'NL' — drives EU/Drittland decisions and the
    // DATEV DebKred Land field
    vendorCountry: varchar('vendorCountry', { length: 2 }),
    totalAmount: integer('totalAmount').notNull(),
    netAmount: integer('netAmount').notNull(),
    vatAmount: integer('vatAmount').notNull(),
    vatRate: decimal('vatRate', { precision: 5, scale: 2 }).notNull(),
    currency: varchar('currency', { length: 3 }).default('EUR').notNull(),
    isReverseCharge: boolean('isReverseCharge').default(false).notNull(),
    status: invoiceStatusEnum('status').default(InvoiceStatus.OPEN).notNull(),
    bookingText: text('bookingText'),
    counterparty: text('counterparty'),
    taxKey: varchar('taxKey', { length: 30 }),
    costCenter1: varchar('costCenter1', { length: 50 }),
    costCenter2: varchar('costCenter2', { length: 50 }),
    comment: text('comment'),
    documentFilenames: text('documentFilenames').array(),
    documentLinks: text('documentLinks').array(),
    processingRunId: varchar('processingRunId', { length: 255 }),
    createdAt: timestamp('createdAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull(),
    updatedAt: timestamp('updatedAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull()
      .$onUpdate(() => new Date())
  },
  (table) => [
    uniqueIndex().on(table.organizationId, table.invoiceNumber),
    index().on(table.organizationId, table.status),
    index().on(table.organizationId, table.bookingDate),
    index().on(table.processingRunId)
  ]
);

// Staging area for uploaded invoice PDFs awaiting chunked AI processing.
// Invoice rows only exist once processing completes, so queued/in-flight
// uploads live here; rows are reconciled to DONE/FAILED at read time and
// DONE rows are purged when the next chunk starts.
export const invoiceUploadTable = pgTable(
  'invoiceUpload',
  {
    id: uuid('id').primaryKey().notNull().defaultRandom(),
    organizationId: uuid('organizationId')
      .notNull()
      .references(() => organizationTable.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade'
      }),
    fileName: varchar('fileName', { length: 255 }).notNull(),
    documentUrl: text('documentUrl').notNull(),
    status: invoiceUploadStatusEnum('status')
      .default(InvoiceUploadStatus.QUEUED)
      .notNull(),
    processingRunId: varchar('processingRunId', { length: 255 }),
    processingStartedAt: timestamp('processingStartedAt', {
      precision: 3,
      mode: 'date'
    }),
    error: text('error'),
    createdAt: timestamp('createdAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull(),
    updatedAt: timestamp('updatedAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull()
      .$onUpdate(() => new Date())
  },
  (table) => [
    index().on(table.organizationId, table.status),
    index().on(table.processingRunId)
  ]
);

export const accountingEntryTable = pgTable(
  'accountingEntry',
  {
    id: uuid('id').primaryKey().notNull().defaultRandom(),
    organizationId: uuid('organizationId')
      .notNull()
      .references(() => organizationTable.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade'
      }),
    invoiceId: uuid('invoiceId').references(() => invoiceTable.id, {
      onDelete: 'set null',
      onUpdate: 'cascade'
    }),
    // Settling bank transactions. An invoice can be paid by several partial
    // transactions, so this is an array; Postgres cannot enforce FKs over
    // array elements — a DB trigger (bank_transaction_delete_cleanup) removes
    // ids of deleted transactions.
    bankTransactionIds: uuid('bankTransactionIds')
      .array()
      .notNull()
      .default(sql`'{}'::uuid[]`),
    bookingText: text('bookingText'),
    documentDate: timestamp('documentDate', { precision: 3, mode: 'date' }).notNull(),
    postingDate: timestamp('postingDate', { precision: 3, mode: 'date' }).notNull(),
    taxKey: varchar('taxKey', { length: 30 }),
    postingStatus: postingStatusEnum('postingStatus')
      .default(PostingStatus.DRAFT)
      .notNull(),
    comment: text('comment'),
    processingRunId: varchar('processingRunId', { length: 255 }),
    // DATEV export linkage
    datevBatchId: uuid('datevBatchId').references(() => datevExportBatchTable.id, {
      onDelete: 'set null',
      onUpdate: 'cascade'
    }),
    datevExportedAt: timestamp('datevExportedAt', { precision: 3, mode: 'date' }),
    datevBelegfeld1: varchar('datevBelegfeld1', { length: 36 }),
    datevBelegfeld2: varchar('datevBelegfeld2', { length: 36 }),
    createdAt: timestamp('createdAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull(),
    updatedAt: timestamp('updatedAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull()
      .$onUpdate(() => new Date())
  },
  (table) => [
    index().on(table.organizationId, table.postingStatus),
    index().on(table.invoiceId),
    index('accountingEntry_bankTransactionIds_gin_idx').using('gin', table.bankTransactionIds),
    index().on(table.processingRunId),
    index().on(table.datevBatchId)
  ]
);

export const accountingEntryLineTable = pgTable(
  'accountingEntryLine',
  {
    id: uuid('id').primaryKey().notNull().defaultRandom(),
    accountingEntryId: uuid('accountingEntryId')
      .notNull()
      .references(() => accountingEntryTable.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade'
      }),
    skrAccountNumber: varchar('skrAccountNumber', { length: 5 }).notNull(),
    debitAmount: integer('debitAmount'), // in cents
    creditAmount: integer('creditAmount'), // in cents
    isVatBooking: boolean('isVatBooking').default(false).notNull(),
    isInitialBooking: boolean('isInitialBooking').default(false).notNull(),
    costCenter1: varchar('costCenter1', { length: 50 }),
    costCenter2: varchar('costCenter2', { length: 50 }),
    comment: text('comment'),
    createdAt: timestamp('createdAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull(),
    updatedAt: timestamp('updatedAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull()
      .$onUpdate(() => new Date())
  },
  (table) => [
    index().on(table.accountingEntryId),
    index().on(table.skrAccountNumber)
  ]
);

// DATEV Export Batch tracking
export const datevExportBatchTable = pgTable(
  'datevExportBatch',
  {
    id: uuid('id').primaryKey().notNull().defaultRandom(),
    organizationId: uuid('organizationId')
      .notNull()
      .references(() => organizationTable.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade'
      }),
    dateFrom: timestamp('dateFrom', { precision: 3, mode: 'date' }).notNull(),
    dateTo: timestamp('dateTo', { precision: 3, mode: 'date' }).notNull(),
    accountingMethodUsed: varchar('accountingMethodUsed', { length: 16 }).notNull(), // 'cash' | 'accrual'
    consultantNumber: varchar('consultantNumber', { length: 12 }).notNull(),
    clientNumber: varchar('clientNumber', { length: 12 }).notNull(),
    formatVersion: varchar('formatVersion', { length: 8 }).notNull(),
    fileKey: varchar('fileKey', { length: 2048 }).notNull(),
    fileHash: varchar('fileHash', { length: 64 }).notNull(),
    rowCount: integer('rowCount').notNull(),
    // Mapper warnings (string[]) — shown in the export history so data
    // problems reach the user instead of dying in task logs
    warnings: jsonb('warnings'),
    createdByUserId: uuid('createdByUserId').references(() => userTable.id, {
      onDelete: 'set null',
      onUpdate: 'cascade'
    }),
    createdAt: timestamp('createdAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull()
  },
  (table) => [
    index('IX_datevExportBatch_org').using(
      'btree',
      table.organizationId.asc().nullsLast().op('uuid_ops')
    ),
    uniqueIndex('IX_datevExportBatch_fileHash_unique').using(
      'btree',
      table.fileHash.asc().nullsLast().op('text_ops')
    )
  ]
);

// Chart of Accounts Customization Table
export const chartOfAccountsCustomizationTable = pgTable(
  'chartOfAccountsCustomization',
  {
    id: uuid('id').primaryKey().notNull().defaultRandom(),
    organizationId: uuid('organizationId')
      .notNull()
      .references(() => organizationTable.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade'
      }),
    templateId: varchar('templateId', { length: 50 }).notNull(), // 'skr03' or 'skr04'
    action: varchar('action', { length: 20 }).notNull(), // 'add', 'hide', 'modify', 'rename'
    accountNumber: varchar('accountNumber', { length: 50 }).notNull(),
    customName: varchar('customName', { length: 255 }),
    parentAccountNumber: varchar('parentAccountNumber', { length: 50 }),
    isActive: boolean('isActive').default(true).notNull(),
    sortOrder: integer('sortOrder'),
    createdAt: timestamp('createdAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull(),
    updatedAt: timestamp('updatedAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull()
      .$onUpdate(() => new Date())
  },
  (table) => [
    index().on(table.organizationId, table.templateId),
    index().on(table.accountNumber),
    uniqueIndex('org_template_account_unique').on(
      table.organizationId,
      table.templateId,
      table.accountNumber
    )
  ]
);

// ----- Invoicing (outgoing e-invoices) -----

/** Party data frozen onto an issued invoice (contact/org rows may change later) */
export type PartySnapshot = {
  name: string;
  line1: string;
  line2?: string;
  postalCode: string;
  city: string;
  country: string;
  vatId?: string;
  taxId?: string;
  email?: string;
  phone?: string;
};

/** EN 16931 BG-23 tax breakdown entry, frozen at issuance. Amounts in cents. */
export type VatBreakdownEntry = {
  categoryCode: string; // UNTDID 5305: S, Z, E, AE, K, ...
  rate: string; // percent, e.g. '19.00'
  basisAmount: number;
  taxAmount: number;
  exemptionReasonText?: string;
};

export const contactTable = pgTable(
  'contact',
  {
    id: uuid('id').primaryKey().notNull().defaultRandom(),
    organizationId: uuid('organizationId')
      .notNull()
      .references(() => organizationTable.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade'
      }),
    type: contactTypeEnum('type').default(ContactType.CUSTOMER).notNull(),
    name: varchar('name', { length: 255 }).notNull(),
    email: varchar('email', { length: 255 }),
    phone: varchar('phone', { length: 32 }),
    addressLine1: varchar('addressLine1', { length: 255 }),
    addressLine2: varchar('addressLine2', { length: 255 }),
    postalCode: varchar('postalCode', { length: 16 }),
    city: varchar('city', { length: 255 }),
    // ISO 3166-1 alpha-2
    country: varchar('country', { length: 2 }),
    vatId: varchar('vatId', { length: 30 }),
    taxId: varchar('taxId', { length: 30 }),
    // Leitweg-ID — mandatory buyer reference for B2G XRechnung
    buyerReference: varchar('buyerReference', { length: 100 }),
    defaultPaymentTermsDays: integer('defaultPaymentTermsDays').default(14).notNull(),
    // SKR debtor account, assigned when the first invoice is issued
    skrAccountNumber: varchar('skrAccountNumber', { length: 5 }),
    notes: text('notes'),
    // Soft delete: contacts with issued invoices must never be hard-deleted
    archivedAt: timestamp('archivedAt', { precision: 3, mode: 'date' }),
    createdAt: timestamp('createdAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull(),
    updatedAt: timestamp('updatedAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull()
      .$onUpdate(() => new Date())
  },
  (table) => [
    index().on(table.organizationId, table.name),
    index().on(table.organizationId, table.type)
  ]
);

export const invoiceNumberRangeTable = pgTable(
  'invoiceNumberRange',
  {
    id: uuid('id').primaryKey().notNull().defaultRandom(),
    organizationId: uuid('organizationId')
      .notNull()
      .references(() => organizationTable.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade'
      }),
    name: varchar('name', { length: 50 }).default('default').notNull(),
    prefix: varchar('prefix', { length: 20 }).default('RE-').notNull(),
    padding: integer('padding').default(4).notNull(),
    // Gapless numbering: incremented via UPDATE ... RETURNING inside the same
    // transaction that flips DRAFT → ISSUED; the row lock serializes issuance
    // and a rollback rolls the counter back with it.
    lastNumber: integer('lastNumber').default(0).notNull(),
    createdAt: timestamp('createdAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull(),
    updatedAt: timestamp('updatedAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull()
      .$onUpdate(() => new Date())
  },
  (table) => [uniqueIndex().on(table.organizationId, table.name)]
);

export const outgoingInvoiceTable = pgTable(
  'outgoingInvoice',
  {
    id: uuid('id').primaryKey().notNull().defaultRandom(),
    organizationId: uuid('organizationId')
      .notNull()
      .references(() => organizationTable.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade'
      }),
    // Optional: invoices can be written without a saved contact — buyer
    // data then lives in buyerSnapshot (invoice-first flow)
    contactId: uuid('contactId').references(() => contactTable.id, {
      onDelete: 'restrict',
      onUpdate: 'cascade'
    }),
    status: outgoingInvoiceStatusEnum('status')
      .default(OutgoingInvoiceStatus.DRAFT)
      .notNull(),
    // NULL while DRAFT — assigned from the number range at issuance
    invoiceNumber: varchar('invoiceNumber', { length: 50 }),
    numberRangeId: uuid('numberRangeId').references(
      () => invoiceNumberRangeTable.id,
      { onDelete: 'set null', onUpdate: 'cascade' }
    ),
    invoiceDate: timestamp('invoiceDate', { precision: 3, mode: 'date' }),
    // Leistungsdatum/-zeitraum (§14 UStG); a single service date uses start only
    servicePeriodStart: timestamp('servicePeriodStart', { precision: 3, mode: 'date' }),
    servicePeriodEnd: timestamp('servicePeriodEnd', { precision: 3, mode: 'date' }),
    dueDate: timestamp('dueDate', { precision: 3, mode: 'date' }),
    buyerReference: varchar('buyerReference', { length: 100 }),
    purchaseOrderReference: varchar('purchaseOrderReference', { length: 100 }),
    currency: varchar('currency', { length: 3 }).default('EUR').notNull(),
    // Document language for the rendered PDF ('de' | 'en'); the CII XML is
    // language-neutral, so this only affects presentation
    language: varchar('language', { length: 2 }).default('de').notNull(),
    // Totals in cents — NULL while DRAFT (computed live from lines), frozen at issuance
    netAmount: integer('netAmount'),
    vatAmount: integer('vatAmount'),
    totalAmount: integer('totalAmount'),
    vatBreakdown: jsonb('vatBreakdown').$type<VatBreakdownEntry[]>(),
    buyerSnapshot: jsonb('buyerSnapshot').$type<PartySnapshot>(),
    sellerSnapshot: jsonb('sellerSnapshot').$type<PartySnapshot>(),
    // Payment details printed on the invoice (IBAN/BIC from the bank account)
    paymentBankAccountId: uuid('paymentBankAccountId').references(
      () => bankAccountDataTable.id,
      { onDelete: 'set null', onUpdate: 'cascade' }
    ),
    introText: text('introText'),
    outroText: text('outroText'),
    notes: text('notes'),
    // BT-120: required free text when lines use category E (steuerfrei);
    // for §19 organizations the fixed Kleinunternehmer note is used instead
    vatExemptionReason: text('vatExemptionReason'),
    // Generated artifacts (S3)
    pdfDocumentUrl: text('pdfDocumentUrl'),
    xmlDocumentUrl: text('xmlDocumentUrl'),
    // Bridge into the booking pipeline: issuance creates a row in `invoice`
    // (and a revenue accountingEntry) via the existing services
    invoiceId: uuid('invoiceId').references(() => invoiceTable.id, {
      onDelete: 'set null',
      onUpdate: 'cascade'
    }),
    // A Storno/Korrektur document points at the invoice it corrects
    correctsInvoiceId: uuid('correctsInvoiceId').references(
      (): AnyPgColumn => outgoingInvoiceTable.id,
      { onDelete: 'restrict', onUpdate: 'cascade' }
    ),
    issuedAt: timestamp('issuedAt', { precision: 3, mode: 'date' }),
    cancelledAt: timestamp('cancelledAt', { precision: 3, mode: 'date' }),
    createdAt: timestamp('createdAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull(),
    updatedAt: timestamp('updatedAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull()
      .$onUpdate(() => new Date())
  },
  (table) => [
    index().on(table.organizationId, table.status),
    index().on(table.contactId),
    // Multiple NULLs are allowed, so drafts don't collide
    uniqueIndex().on(table.organizationId, table.invoiceNumber),
    uniqueIndex().on(table.invoiceId)
  ]
);

export const outgoingInvoiceLineTable = pgTable(
  'outgoingInvoiceLine',
  {
    id: uuid('id').primaryKey().notNull().defaultRandom(),
    outgoingInvoiceId: uuid('outgoingInvoiceId')
      .notNull()
      .references(() => outgoingInvoiceTable.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade'
      }),
    position: integer('position').notNull(),
    description: text('description').notNull(),
    quantity: decimal('quantity', { precision: 12, scale: 3 }).default('1').notNull(),
    // UN/ECE Recommendation 20 code (C62 = piece, HUR = hour, DAY = day, ...)
    unit: varchar('unit', { length: 8 }).default('C62').notNull(),
    // EN 16931 allows >2 decimals on unit prices (rounding happens per line);
    // deliberately NOT integer cents, unlike all total amounts
    unitPrice: decimal('unitPrice', { precision: 14, scale: 4 }).notNull(),
    // UNTDID 5305: S, Z, E, AE, K, ...
    vatCategoryCode: varchar('vatCategoryCode', { length: 4 }).default('S').notNull(),
    vatRate: decimal('vatRate', { precision: 5, scale: 2 }).default('19.00').notNull(),
    // Rounded line net in cents
    lineNetAmount: integer('lineNetAmount').notNull(),
    createdAt: timestamp('createdAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull(),
    updatedAt: timestamp('updatedAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull()
      .$onUpdate(() => new Date())
  },
  (table) => [index().on(table.outgoingInvoiceId)]
);

// Billing tables from upstream
export const subscriptionTable = pgTable('subscription', {
  id: text('id').primaryKey(),
  organizationId: uuid('organizationId').notNull(),
  status: varchar('status', { length: 64 }).notNull(),
  active: boolean('active').notNull().default(false),
  provider: varchar('provider', { length: 32 }).notNull(),
  cancelAtPeriodEnd: boolean('cancelAtPeriodEnd').notNull().default(false),
  currency: varchar('currency', { length: 3 }).notNull(),
  periodStartsAt: timestamp('periodStartsAt', { withTimezone: true, precision: 6 }).notNull(),
  periodEndsAt: timestamp('periodEndsAt', { withTimezone: true, precision: 6 }).notNull(),
  trialStartsAt: timestamp('trialStartsAt', { withTimezone: true, precision: 6 }),
  trialEndsAt: timestamp('trialEndsAt', { withTimezone: true, precision: 6 }),
  createdAt: timestamp('createdAt', { withTimezone: true }).notNull().defaultNow(),
  updatedAt: timestamp('updatedAt', { withTimezone: true }).notNull().defaultNow(),
}, (table) => [
  index('IX_subscription_organizationId').using(
    'btree',
    table.organizationId.asc().nullsLast().op('uuid_ops')
  )
]);

export const subscriptionItemTable = pgTable('subscriptionItem', {
  id: text('id').primaryKey(),
  subscriptionId: text('subscriptionId').notNull(),
  quantity: integer('quantity').notNull(),
  productId: text('productId').notNull(),
  variantId: text('variantId').notNull(),
  priceAmount: doublePrecision('priceAmount'),
  interval: text('interval').notNull(),
  intervalCount: integer('intervalCount').notNull(),
  type: text('type'),
  model: text('model'),
}, (table) => [
  index('IX_subscriptionItem_subscriptionId').using(
    'btree',
    table.subscriptionId.asc().nullsLast()
  )
]);

export const orderTable = pgTable('order', {
  id: text('id').primaryKey(),
  organizationId: uuid('organizationId').notNull(),
  status: varchar('status', { length: 64 }).notNull(),
  provider: varchar('provider', { length: 32 }).notNull(),
  totalAmount: doublePrecision('totalAmount').notNull(),
  currency: varchar('currency', { length: 3 }).notNull(),
  createdAt: timestamp('createdAt', { withTimezone: true }).notNull().defaultNow(),
  updatedAt: timestamp('updatedAt', { withTimezone: true }).notNull().defaultNow(),
},
  (table) => [
    index('IX_order_organizationId').using(
      'btree',
      table.organizationId.asc().nullsLast().op('uuid_ops')
    )
  ]);

export const orderItemTable = pgTable('orderItem', {
  id: text('id').primaryKey(),
  orderId: text('orderId').notNull(),
  quantity: integer('quantity').notNull(),
  productId: text('productId').notNull(),
  variantId: text('variantId').notNull(),
  priceAmount: doublePrecision('priceAmount'),
  type: text('type'),
  model: text('model'),
},
  (table) => [
    index('IX_orderItem_orderId').using(
      'btree',
      table.orderId.asc().nullsLast()
    )
  ]);

// Usage Events Table for local usage tracking with Stripe checkpoint approach
export enum UsageEventType {
  AI_INVOICE_PROCESSING = 'ai_invoice_processing',
  STRIPE_SYNC_CHECKPOINT = 'stripe_sync_checkpoint'
}

export enum UsageEventSource {
  LOCAL = 'local',
  STRIPE = 'stripe'
}

export const usageEventTypeEnum = pgEnum('usageeventtype', enumToPgEnum(UsageEventType));
export const usageEventSourceEnum = pgEnum('usageeventsource', enumToPgEnum(UsageEventSource));

export const usageEventsTable = pgTable('usageEvents', {
  id: uuid('id').primaryKey().notNull().defaultRandom(),
  organizationId: uuid('organizationId')
    .notNull()
    .references(() => organizationTable.id, {
      onDelete: 'cascade',
      onUpdate: 'cascade'
    }),
  eventType: usageEventTypeEnum('eventType').notNull(),
  source: usageEventSourceEnum('source').notNull(),
  subscriptionPeriodStart: timestamp('subscriptionPeriodStart', { precision: 3, mode: 'date' }).notNull(),
  subscriptionPeriodEnd: timestamp('subscriptionPeriodEnd', { precision: 3, mode: 'date' }).notNull(),
  quantity: integer('quantity').notNull().default(1),
  // Store cost in cents (e.g., €0.05 = 5 cents)
  incrementalCost: integer('incrementalCost').notNull().default(0),
  // For Stripe checkpoints - total usage count and cost in the period
  totalUsageCount: integer('totalUsageCount'), // Only for stripe_sync_checkpoint events
  totalCost: integer('totalCost'), // Only for stripe_sync_checkpoint events, in cents
  // Stripe sync tracking
  stripeIdempotencyKey: varchar('stripeIdempotencyKey', { length: 255 }),
  stripeSyncedAt: timestamp('stripeSyncedAt', { precision: 3, mode: 'date' }),
  // Raw Stripe data for debugging/auditing
  stripeMetadata: jsonb('stripeMetadata'),
  createdAt: timestamp('createdAt', { precision: 3, mode: 'date' })
    .defaultNow()
    .notNull()
}, (table) => [
  // Index for fast spending limit checks
  index('IX_usageEvents_org_period').using(
    'btree',
    table.organizationId.asc().nullsLast().op('uuid_ops'),
    table.subscriptionPeriodStart.asc().nullsLast().op('timestamp_ops'),
    table.subscriptionPeriodEnd.asc().nullsLast().op('timestamp_ops')
  ),
  // Index for sync operations
  index('IX_usageEvents_sync').using(
    'btree',
    table.stripeSyncedAt.asc().nullsLast().op('timestamp_ops')
  ),
  // Ensure idempotency for Stripe events
  uniqueIndex('IX_usageEvents_stripe_idempotency').using(
    'btree',
    table.stripeIdempotencyKey.asc().nullsLast().op('text_ops')
  ).where(sql`${table.stripeIdempotencyKey} IS NOT NULL`)
]);

// contactToContactTagTable removed during CRM cleanup



// Relations
export const apiKeyRelations = relations(apiKeyTable, ({ one }) => ({
  organization: one(organizationTable, {
    fields: [apiKeyTable.organizationId],
    references: [organizationTable.id],
  }),
}));

export const organizationRelations = relations(
  organizationTable,
  ({ many }) => ({
    apiKeys: many(apiKeyTable),
    // contacts: many(contactTable), // Removed during CRM cleanup
    invitations: many(invitationTable),
    feedbacks: many(feedbackTable),
    // workHours: many(workHoursTable),
    // workTimeSlots: many(workTimeSlotTable),
    webhooks: many(webhookTable),
    memberships: many(membershipTable),
    bankAccounts: many(bankAccountDataTable),
    invoices: many(invoiceTable),
    accountingEntries: many(accountingEntryTable),
    usageEvents: many(usageEventsTable)
  })
);

export const authenticatorAppRelations = relations(
  authenticatorAppTable,
  ({ one }) => ({
    user: one(userTable, {
      fields: [authenticatorAppTable.userId],
      references: [userTable.id]
    })
  })
);

export const userRelations = relations(userTable, ({ many }) => ({
  authenticatorApps: many(authenticatorAppTable),
  changeEmailRequests: many(changeEmailRequestTable),
  // Contact-related relations removed during CRM cleanup:
  // contactComments: many(contactCommentTable),
  // contactNotes: many(contactNoteTable),
  // contactPageVisits: many(contactPageVisitTable),
  // favorites: many(favoriteTable),
  accounts: many(accountTable),
  feedbacks: many(feedbackTable),
  notifications: many(notificationTable),
  sessions: many(sessionTable),
  memberships: many(membershipTable)
}));

export const changeEmailRequestRelations = relations(
  changeEmailRequestTable,
  ({ one }) => ({
    user: one(userTable, {
      fields: [changeEmailRequestTable.userId],
      references: [userTable.id]
    })
  })
);

export const invitationRelations = relations(invitationTable, ({ one }) => ({
  organization: one(organizationTable, {
    fields: [invitationTable.organizationId],
    references: [organizationTable.id]
  })
}));

export const accountRelations = relations(accountTable, ({ one }) => ({
  user: one(userTable, {
    fields: [accountTable.userId],
    references: [userTable.id]
  })
}));

export const feedbackRelations = relations(feedbackTable, ({ one }) => ({
  organization: one(organizationTable, {
    fields: [feedbackTable.organizationId],
    references: [organizationTable.id]
  }),
  user: one(userTable, {
    fields: [feedbackTable.userId],
    references: [userTable.id]
  })
}));

export const notificationRelations = relations(
  notificationTable,
  ({ one }) => ({
    user: one(userTable, {
      fields: [notificationTable.userId],
      references: [userTable.id]
    })
  })
);

export const sessionRelations = relations(sessionTable, ({ one }) => ({
  user: one(userTable, {
    fields: [sessionTable.userId],
    references: [userTable.id]
  })
}));

export const membershipRelations = relations(membershipTable, ({ one }) => ({
  user: one(userTable, {
    fields: [membershipTable.userId],
    references: [userTable.id]
  }),
  organization: one(organizationTable, {
    fields: [membershipTable.organizationId],
    references: [organizationTable.id]
  })
}));

export const organizationLogoRelations = relations(
  organizationLogoTable,
  ({ one }) => ({
    organization: one(organizationTable, {
      fields: [organizationLogoTable.organizationId],
      references: [organizationTable.id]
    })
  })
);

export const subscriptionRelations = relations(subscriptionTable, ({ one }) => ({
  organization: one(organizationTable, {
    fields: [subscriptionTable.organizationId],
    references: [organizationTable.id]
  })
}));

export const subscriptionItemRelations = relations(subscriptionItemTable, ({ one }) => ({
  organization: one(subscriptionTable, {
    fields: [subscriptionItemTable.subscriptionId],
    references: [subscriptionTable.id]
  })
}));

export const orderRelations = relations(orderTable, ({ one }) => ({
  organization: one(organizationTable, {
    fields: [orderTable.organizationId],
    references: [organizationTable.id]
  })
}));

export const orderItemRelations = relations(orderItemTable, ({ one }) => ({
  organization: one(orderTable, {
    fields: [orderItemTable.orderId],
    references: [orderTable.id]
  })
}));

export const bankAccountDataRelations = relations(bankAccountDataTable, ({ one, many }) => ({
  organization: one(organizationTable, {
    fields: [bankAccountDataTable.organizationId],
    references: [organizationTable.id]
  }),
  bankConnection: one(bankConnectionTable, {
    fields: [bankAccountDataTable.bankConnectionId],
    references: [bankConnectionTable.id]
  }),
  transactions: many(bankTransactionTable)
}));

export const bankTransactionRelations = relations(bankTransactionTable, ({ one }) => ({
  bankAccount: one(bankAccountDataTable, {
    fields: [bankTransactionTable.bankAccountDataId],
    references: [bankAccountDataTable.id]
  })
}));

export const invoiceRelations = relations(invoiceTable, ({ one }) => ({
  organization: one(organizationTable, {
    fields: [invoiceTable.organizationId],
    references: [organizationTable.id]
  })
}));

export const accountingEntryRelations = relations(accountingEntryTable, ({ one, many }) => ({
  organization: one(organizationTable, {
    fields: [accountingEntryTable.organizationId],
    references: [organizationTable.id]
  }),
  invoice: one(invoiceTable, {
    fields: [accountingEntryTable.invoiceId],
    references: [invoiceTable.id]
  }),
  // No bankTransaction relation: bankTransactionIds is an array column and
  // drizzle relations cannot follow it — loaders fetch transactions manually.
  lines: many(accountingEntryLineTable)
}));

export const accountingEntryLineRelations = relations(accountingEntryLineTable, ({ one }) => ({
  accountingEntry: one(accountingEntryTable, {
    fields: [accountingEntryLineTable.accountingEntryId],
    references: [accountingEntryTable.id]
  })
}));

// Add relations for bankConnectionTable
export const bankConnectionRelations = relations(bankConnectionTable, ({ one, many }) => ({
  organization: one(organizationTable, {
    fields: [bankConnectionTable.organizationId],
    references: [organizationTable.id]
  }),
  accounts: many(bankAccountDataTable)
}));

// Add relations for usageEventsTable
export const usageEventsRelations = relations(usageEventsTable, ({ one }) => ({
  organization: one(organizationTable, {
    fields: [usageEventsTable.organizationId],
    references: [organizationTable.id]
  })
}));

export const webhookRelations = relations(webhookTable, ({ one }) => ({
  organization: one(organizationTable, {
    fields: [webhookTable.organizationId],
    references: [organizationTable.id]
  })
}));

// Invoicing relations
export const contactRelations = relations(contactTable, ({ one, many }) => ({
  organization: one(organizationTable, {
    fields: [contactTable.organizationId],
    references: [organizationTable.id]
  }),
  outgoingInvoices: many(outgoingInvoiceTable)
}));

export const invoiceNumberRangeRelations = relations(
  invoiceNumberRangeTable,
  ({ one }) => ({
    organization: one(organizationTable, {
      fields: [invoiceNumberRangeTable.organizationId],
      references: [organizationTable.id]
    })
  })
);

export const outgoingInvoiceRelations = relations(
  outgoingInvoiceTable,
  ({ one, many }) => ({
    organization: one(organizationTable, {
      fields: [outgoingInvoiceTable.organizationId],
      references: [organizationTable.id]
    }),
    contact: one(contactTable, {
      fields: [outgoingInvoiceTable.contactId],
      references: [contactTable.id]
    }),
    numberRange: one(invoiceNumberRangeTable, {
      fields: [outgoingInvoiceTable.numberRangeId],
      references: [invoiceNumberRangeTable.id]
    }),
    paymentBankAccount: one(bankAccountDataTable, {
      fields: [outgoingInvoiceTable.paymentBankAccountId],
      references: [bankAccountDataTable.id]
    }),
    invoice: one(invoiceTable, {
      fields: [outgoingInvoiceTable.invoiceId],
      references: [invoiceTable.id]
    }),
    corrects: one(outgoingInvoiceTable, {
      fields: [outgoingInvoiceTable.correctsInvoiceId],
      references: [outgoingInvoiceTable.id],
      relationName: 'correction'
    }),
    correctedBy: many(outgoingInvoiceTable, { relationName: 'correction' }),
    lines: many(outgoingInvoiceLineTable)
  })
);

export const outgoingInvoiceLineRelations = relations(
  outgoingInvoiceLineTable,
  ({ one }) => ({
    outgoingInvoice: one(outgoingInvoiceTable, {
      fields: [outgoingInvoiceLineTable.outgoingInvoiceId],
      references: [outgoingInvoiceTable.id]
    })
  })
);
